import { useRef, useState, useContext } from "react";
import { WebSocketContext } from "../stores/websocketContext.js";

export function useWebSocketChat({
  setCompleteAudio,
  onStreamingStart,
  onStreamingText,
  onStreamingAudio,
  onStreamingComplete,
  onStreamingError,
  onTranscription,
  onConversationHistory,
}) {
  const { setGlobalConnectionStatus } = useContext(WebSocketContext);

  const [isAuthenticated, setIsAuthenticated] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("Disconnected");
  const [botStatus, setBotStatus] = useState(null);
  const storedVoice = localStorage.getItem("selectedVoice") || "default";
  const selectedExpert =
    localStorage.getItem("selectedExpert") || "psychologist";

  const audioChunksRef = useRef([]);
  const socketRef = useRef(null);
  const personaRef = useRef("");
  const patientRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const heartbeatIntervalRef = useRef(null);
  const voiceRef = useRef("");
  const MAX_RECONNECTS = 5;
  const manualDisconnectRef = useRef(false);
  const pendingAudioRef = useRef(null);
  const conversationId = useRef(null);
  if (!personaRef.current) personaRef.current = selectedExpert;
  if (!voiceRef.current) voiceRef.current = storedVoice;

  const handleWebSocketMessage = (data) => {
    if (!data) return;

    switch (data.type) {
      case "ping":
        console.log("💓 Ping received, sending pong");
        socketRef.current?.send(JSON.stringify({ type: "pong" }));
        return;

      case "pong":
        console.log("💓 Pong received");
        return;

      case "conversation_history":
        onConversationHistory?.(data);
        return;

      case "auth_success":
        setIsAuthenticated(true);
        return;

      case "streaming_start":
        onStreamingStart?.(data);
        return;

      case "streaming_text":
        onStreamingText(data.text);
        return;

      case "streaming_audio":
        onStreamingAudio?.(data);
        return;

      case "complete_audio":
        setCompleteAudio?.(data.audio);
        return;

      case "streaming_complete":
        onStreamingComplete?.(data);
        return;

      case "streaming_error":
        console.error("❌ Streaming error:", data.error);
        onStreamingError?.(data);
        return;

      case "transcription":
        onTranscription?.(data.text);
        return;

      case "error":
        console.error("❌ Error:", data.message);
        return;

      default:
        if (data.processing_time) {
          console.log(`⏱️ Processing time: ${data.processing_time}`);
        }
    }
  };

  const updateConnectionStatus = (status) => {
    setConnectionStatus(status);
    if (setGlobalConnectionStatus) {
      setGlobalConnectionStatus(status);
    }
  };

  const getActualVoice = (voice) => {
    if (voice.current === "default") {
      return "onyx";
    }

    const validVoices = ["nova", "shimmer", "echo", "onyx", "fable", "alloy"];
    if (validVoices.includes(voice.current)) {
      return voice.current;
    }

    return personaRef.current === "psychologist" ? "onyx" : "shimmer";
  };

  const connect = () => {
    if (!patientRef.current) {
      console.warn("Cannot connect WebSocket: patient ID is not set.");
      return;
    }
    if (!personaRef.current) {
      console.warn("Cannot connect WebSocket: persona (expert) is not set.");
      return;
    }

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      console.log("🟡 WebSocket already connected");
      return;
    }

    manualDisconnectRef.current = false;
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${process.env.REACT_APP_DOCTOR_URL}chat-final3/${patientRef.current}/${personaRef.current}`;

    const newSocket = new WebSocket(wsUrl);

    newSocket.onopen = () => {
      console.log("🔌 WebSocket connected");
      socketRef.current = newSocket;
      updateConnectionStatus("Connected");
      // setConnectionStatus('Connected');
      reconnectAttemptsRef.current = 0;
      startHeartbeat(newSocket);

      const authPayload = {
        token: process.env.REACT_APP_DOCTOR_TOKEN,
        voice: getActualVoice(voiceRef),
      };
      newSocket.send(JSON.stringify(authPayload));

      if (pendingAudioRef.current) {
        console.log("📤 Sending pending audio after reconnect");
        newSocket.send(JSON.stringify({ audio: pendingAudioRef.current }));
        pendingAudioRef.current = null;
      }
    };

    newSocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      } catch (err) {
        console.error("❌ Failed to parse WebSocket message:", err);
      }
    };

    newSocket.onclose = (event) => {
      console.log("🔌 WebSocket closed:", event, event.reason);
      socketRef.current = null;
      // setConnectionStatus('Disconnected');
      updateConnectionStatus("Disconnected");
      stopHeartbeat();

      if (
        event.code !== 1000 &&
        reconnectAttemptsRef.current < MAX_RECONNECTS &&
        !manualDisconnectRef.current
      ) {
        reconnectAttemptsRef.current++;
        const retryDelay = 2000 * reconnectAttemptsRef.current;
        setTimeout(connect, retryDelay);
      }
    };

    newSocket.onerror = (err) => {
      console.error("❌ WebSocket error:", err);
    };
  };

  const updatePatientId = (id) => {
    if (patientRef.current !== id) {
      patientRef.current = id;
      if (socketRef.current) {
        socketRef.current.close(1000, "Patient ID changed");
      }
      connect();
    }
  };

  const sendMessage = (text, audioEmotion = null, imageEmotion = null) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.error("❌ Cannot send message: WebSocket not connected");
      onStreamingError?.("WebSocket not connected");
      return false;
    }

    if (!isAuthenticated) {
      console.error("❌ Cannot send message: Not authenticated");
      onStreamingError?.("Not authenticated");
      return false;
    }

    if (!text?.trim()) {
      console.error("❌ Cannot send empty message");
      return false;
    }

    const messagePayload = {
      text: text.trim(),
      persona: personaRef.current,
      voice: getActualVoice(voiceRef.current),
      ...(audioEmotion && { audio_emotion: audioEmotion }),
      ...(imageEmotion && { image_emotion: imageEmotion }),
      conversation_id: conversationId.current,
    };

    try {
      socketRef.current.send(JSON.stringify(messagePayload));
      return true;
    } catch (error) {
      console.error("❌ Error sending message:", error);
      onStreamingError?.("Failed to send message");
      return false;
    }
  };

  const sendAudio = async () => {
    try {
      setBotStatus("thinking");
      const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm",
      });

      audioChunksRef.current = [];

      if (audioBlob.size < 2000) {
        console.warn("Recording too short or empty. Not sending.");
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        try {
          const result = reader.result;
          if (!result || typeof result !== "string") {
            throw new Error("FileReader returned invalid result");
          }

          const base64Audio = result.split(",")[1];
          if (!base64Audio) {
            throw new Error("Failed to extract base64 audio from result");
          }

          if (socketRef.current && socketRef.current.readyState === 1) {
            socketRef.current.send(JSON.stringify({ audio: base64Audio }));
            setBotStatus("thinking");
            console.log("✅ Audio sent to server");
          } else {
            console.warn("🕓 WebSocket not connected, queueing audio");
            pendingAudioRef.current = base64Audio;
            setBotStatus("queued");
            connect();
          }
        } catch (err) {
          console.error("Error inside FileReader.onload:", err);
        }
      };

      reader.onerror = (err) => {
        console.error("FileReader error:", err);
      };

      reader.readAsDataURL(audioBlob);
    } catch (err) {
      console.error("Error in sendAudio:", err);
    }
  };

  const startHeartbeat = (ws) => {
    stopHeartbeat();
    heartbeatIntervalRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "ping" }));
        console.log("💓 Ping sent");
      }
    }, 30000);
  };

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  const updatePersona = (newPersona) => {
    if (personaRef.current !== newPersona) {
      personaRef.current = newPersona;

      if (socketRef.current) {
        socketRef.current.onclose = () => {
          console.log(`🔄 Reconnecting with model: ${newPersona}`);
          connect();
        };
        socketRef.current.close(1000, "Persona changed");
      } else {
        connect();
      }
    }
  };

  const updateVoice = (newVoice) => {
    if (voiceRef.current !== newVoice) {
      voiceRef.current = newVoice;

      if (socketRef.current) {
        socketRef.current.onclose = () => {
          console.log(`🔄 Reconnecting with voice: ${newVoice}`);
          connect();
        };
        socketRef.current.close(1000, "Voice changed");
      } else {
        connect();
      }
    }
  };

  const updateConversationId = (newId) => {
    if (conversationId.current !== newId) {
      conversationId.current = newId;
    }

    if (socketRef.current) {
      socketRef.current.onclose = () => {
        console.log(`🔄 Reconnecting with voice: ${newId}`);
        connect();
      };
      socketRef.current.close(1000, "Voice changed");
    } else {
      connect();
    }
  };

  const disconnectWebSocket = () => {
    if (socketRef && socketRef.readyState === WebSocket.OPEN) {
      manualDisconnectRef.current = true;
      console.log("🔌 Manual disconnect requested");
      socketRef.current.send(JSON.stringify({ type: "disconnect_request" }));
      socketRef.current.close(1000, "Manual disconnect");
    }
  };

  return {
    isAuthenticated,
    sendMessage,
    socket: socketRef.current,
    connectionStatus,
    connectWebSocket: connect,
    voiceRef,
    updatePersona,
    updatePatientId,
    disconnectWebSocket,
    sendAudio,
    audioChunksRef,
    setBotStatus,
    botStatus,
    updateVoice,
    updateConversationId,
  };
}
